import { Dispatch, RefObject, SetStateAction, useCallback } from 'react';

import {
  FrameAction,
  FrameActionType,
} from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/types';
import { BoxRow } from '@/features/broadcast-schedule-program/types';
import { TRect } from '@/types';
import { convertISOToSeconds } from '@/utils/hh-mm-ss-into-number';

export const VIDEO_OFFSET_TIME_CONTROL = 0.15;

type PreviewablePresenceBox = {
  start_datetime: string;
  end_datetime: string;
  entity_label?: string | null;
  entity_score?: number | null;
  parameters?: {
    bbox: TRect;
  } | null;
};

type UsePresencePreviewBoxParams = {
  referenceStartDatetime?: string;
  setVisibleBox: Dispatch<SetStateAction<BoxRow | null>>;
  videoControlRef: RefObject<{
    seekToSecond: (second: number) => void;
  } | null>;
};

export function usePresencePreviewBox({
  referenceStartDatetime,
  setVisibleBox,
  videoControlRef,
}: UsePresencePreviewBoxParams) {
  return useCallback(
    (
      box: PreviewablePresenceBox,
      frame: FrameActionType = FrameAction.START,
      currentFrame?: number
    ) => {
      if (!box.parameters || !referenceStartDatetime) return null;

      const referenceDate = new Date(referenceStartDatetime);
      const seconds =
        {
          [FrameAction.START]:
            convertISOToSeconds(box.start_datetime, referenceDate) + VIDEO_OFFSET_TIME_CONTROL,
          [FrameAction.END]:
            convertISOToSeconds(box.end_datetime, referenceDate) - VIDEO_OFFSET_TIME_CONTROL,
          [FrameAction.CURRENT]: currentFrame || 0,
        }[frame] ?? 0;

      const visibleBox: BoxRow = {
        bbox: box.parameters.bbox,
        entity_label: box.entity_label || '',
        frame: seconds,
        entity_score: box.entity_score || 0,
      };

      setVisibleBox(visibleBox);
      videoControlRef.current?.seekToSecond(seconds);

      return { seconds, visibleBox };
    },
    [referenceStartDatetime, setVisibleBox, videoControlRef]
  );
}
